import { Descriptions, Drawer, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { LogItem, ObjectTypeChoices } from '@/models/logItem';

const { Text } = Typography;

interface AuditLogDrawerProps {
  logItem: LogItem | null;
  onClose: () => void;
}

function deriveResult(actionType: string | null): 'Success' | 'Failed' {
  return actionType?.includes('FAILURE') ? 'Failed' : 'Success';
}

function objectLink(objectType: string | null, objectId: string | null) {
  if (!objectId || !objectType) return null;
  if (objectType === ObjectTypeChoices.STUDY) {
    return <Link to={`/studies/${objectId}`}>{objectId}</Link>;
  }
  if (objectType === ObjectTypeChoices.REPORT) {
    return <Link to={`/reports/${objectId}`}>{objectId}</Link>;
  }
  if (objectType === ObjectTypeChoices.USER) {
    return <Link to="/admin/users">{objectId}</Link>;
  }
  return <Text copyable>{objectId}</Text>;
}

export default function AuditLogDrawer({
  logItem,
  onClose,
}: AuditLogDrawerProps) {
  const result = deriveResult(logItem?.actionType ?? null);

  return (
    <Drawer
      title="Event Detail"
      placement="right"
      width={480}
      open={!!logItem}
      onClose={onClose}
    >
      {logItem && (
        <>
          <Descriptions
            column={1}
            size="small"
            bordered
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label="Event ID">
              <Text copyable code style={{ fontSize: 12 }}>
                {logItem.uuid}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Time">
              {dayjs(logItem.createdAt).format('MMM D, YYYY h:mm:ss A')}
            </Descriptions.Item>
            <Descriptions.Item label="User">
              {logItem.userFirstName || logItem.userLastName
                ? `${logItem.userFirstName ?? ''} ${logItem.userLastName ?? ''}`.trim()
                : (logItem.userId ?? '—')}
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              {logItem.userRole ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Action">
              {logItem.actionType ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Result">
              <Tag color={result === 'Failed' ? 'error' : 'success'}>
                {result}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Object Type">
              {logItem.objectType ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Object ID">
              {objectLink(logItem.objectType, logItem.objectId) ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Source (IP)">
              {logItem.ipAddress ?? '—'}
            </Descriptions.Item>
          </Descriptions>

          {logItem.context && Object.keys(logItem.context).length > 0 && (
            <>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                Event Payload
              </Typography.Title>
              <Descriptions column={1} size="small" bordered>
                {Object.entries(logItem.context).map(([key, value]) => (
                  <Descriptions.Item key={key} label={key}>
                    <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                      {typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : String(value ?? '')}
                    </Text>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </>
          )}
        </>
      )}
    </Drawer>
  );
}
